import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';
import { Worker } from '../../types/worker';

interface AdvancePayTabProps {
  farmId: string;
}

const AdvancePayTab: React.FC<AdvancePayTabProps> = ({ farmId }) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [advances, setAdvances] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [advanceAmount, setAdvanceAmount] = useState(0);

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [workersData, advancesRes] = await Promise.all([
        fetchWorkers(farmId),
        supabase.from('advance_pay_requests').select('*').eq('farm_id', farmId)
      ]);
      setWorkers(workersData);
      setAdvances(advancesRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdvanceDialog = (worker: Worker) => {
    setSelectedWorker(worker);
    setAdvanceAmount(0);
    setAdvanceDialogOpen(true);
  };

  const handleRequestAdvance = async () => {
    if (!selectedWorker || !advanceAmount || advanceAmount <= 0) return;
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.from('advance_pay_requests').insert([
        {
          worker_id: selectedWorker.id,
          farm_id: farmId,
          amount: advanceAmount,
          requested_at: new Date().toISOString(),
          status: 'pending',
        },
      ]);
      if (error) throw error;
      setSuccess('Advance request submitted!');
      setAdvanceDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>Advance Pay Management</Typography>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Base Pay</TableCell>
                  <TableCell>Advance Taken</TableCell>
                  <TableCell>Available Advance</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workers.map(worker => {
                  const workerAdvances = advances.filter(a => a.worker_id === worker.id && a.status !== 'rejected');
                  const totalAdvance = workerAdvances.reduce((sum, a) => sum + (a.amount || 0), 0);
                  const available = (worker.wage || 0) / 2 - totalAdvance;
                  return (
                    <TableRow key={worker.id}>
                      <TableCell>{worker.name}</TableCell>
                      <TableCell>{worker.wage?.toLocaleString() || '-'}</TableCell>
                      <TableCell>{totalAdvance > 0 ? `-${totalAdvance.toLocaleString()}` : '0'}</TableCell>
                      <TableCell>{available > 0 ? available.toLocaleString() : '0'}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => handleOpenAdvanceDialog(worker)} disabled={available <= 0}>
                          Request Advance
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Dialog open={advanceDialogOpen} onClose={() => setAdvanceDialogOpen(false)}>
        <DialogTitle>Request Advance Pay</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Worker: {selectedWorker?.name}
          </Typography>
          <TextField
            label="Advance Amount"
            type="number"
            fullWidth
            value={advanceAmount}
            onChange={e => setAdvanceAmount(Number(e.target.value))}
            inputProps={{ min: 0, max: selectedWorker ? (selectedWorker.wage || 0) / 2 : 0 }}
            helperText={selectedWorker ? `Max: ${(selectedWorker.wage / 2).toLocaleString()}` : ''}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can request up to 50% of base pay as advance. This will be deducted from end-of-month pay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRequestAdvance} variant="contained" disabled={advanceAmount <= 0 || !selectedWorker}>
            Request Advance
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancePayTab;
